import { createId } from '@paralleldrive/cuid2'
import type { HarmonyPackageBoard } from '@rhythmic/db/types'
import { HarmonyPackageBoardSchema } from '@rhythmic/types'
import type { Collection, Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'
import { HarmonyPackageBoardColumnSchema } from '../../collections'

import { getBoardsCollection, getColumnsCollection } from '../getCollections'

const logger = useRhythmicLogger('useHarmonyPlanContext:createBoard')

export interface HarmonyPlanBoardColumnCreateInput {
  id?: string
  name: string
  position?: number | null
  statusType?: string | null
  workspace_id?: string | null
  context?: Record<string, unknown> | null
}

export interface CreateHarmonyPlanBoardPayload {
  name: string
  image?: string | null
  context?: Record<string, unknown> | null
  iterationId?: string | null
  publishStatus?: string | null
  forecastStartDate?: string | null
  forecastEndDate?: string | null
  forecastBurndown?: number[] | null
  costPerIteration?: number | null
  columns?: {
    create: HarmonyPlanBoardColumnCreateInput[]
  }
  folderId?: string | null
}

export interface HarmonyPlanBoardMutationContext {
  workspaceId: string
  planId: string
  collection?: Collection<HarmonyPackageBoard>
}

function validateParams(_payload: CreateHarmonyPlanBoardPayload, context: HarmonyPlanBoardMutationContext) {
  const { workspaceId, planId } = context

  if (!workspaceId) throw new Error('Workspace identifier is required to create a harmony plan board')

  if (!planId) throw new Error('Harmony plan identifier is required to create a board')

  return { workspaceId, planId }
}

export function handleCreateHarmonyPlanBoard(
  payload: CreateHarmonyPlanBoardPayload,
  context: HarmonyPlanBoardMutationContext,
): Transaction {
  const { workspaceId, planId } = validateParams(payload, context)
  logger.debug('Parameters validated for handleCreateHarmonyPlanBoard', {
    payload,
    context,
  })
  const boardCollection = getBoardsCollection(planId)
  const boardId = `board_${createId()}`
  const createBoardTx = createOptimisticAction<{
    payload: CreateHarmonyPlanBoardPayload
    context: HarmonyPlanBoardMutationContext
  }>({
    onMutate: (m) => {
      logger.debug('Optimistically creating a new harmony plan board', {
        context,
        payload,
      })
      const newBoard: HarmonyPackageBoard = HarmonyPackageBoardSchema.parse({
        id: boardId,
        name: m.payload.name,
        image: m.payload.image ?? null,
        workspace_id: m.context.workspaceId,
        package_id: m.context.planId,
        folder_id: m.payload.folderId ?? null,
      })

      logger.debug('Inserting new board optimistically into collection', {
        newBoard,
      })
      boardCollection.insert(newBoard)
      const columnsCollection = getColumnsCollection(newBoard.id)
      const newColums = m.payload.columns?.create.map((col, index) => {
        return HarmonyPackageBoardColumnSchema.parse({
          id: col.id ?? `column_${createId()}`,
          board_id: newBoard.id,
          name: col.name,
          position: col.position ?? index,
          status_type: col.statusType ?? 'default',
          workspace_id: col.workspace_id ?? m.context.workspaceId,
        })
      })
      if (newColums) columnsCollection.insert(newColums)
    },
    mutationFn: async (m) => {
      const result = await $fetch<{ board: HarmonyPackageBoard; txid: string }>('/api/harmony-package-boards', {
        method: 'POST',
        body: {
          id: boardId,
          name: m.payload.name,
          image: m.payload.image,
          iteration_id: m.payload.iterationId,
          publish_status: m.payload.publishStatus,
          forecast_start_date: m.payload.forecastStartDate,
          forecast_end_date: m.payload.forecastEndDate,
          forecast_burndown: m.payload.forecastBurndown,
          cost_per_iteration: m.payload.costPerIteration,
          package_id: planId,
          workspace_id: workspaceId,
          folder_id: m.payload.folderId,
          columns: m.payload.columns,
        },
      })

      if (!boardCollection.utils.awaitTxId) return result

      logger.debug('Awaiting transaction confirmation for createHarmonyPlanBoard', { txid: result })
      await boardCollection.utils.awaitTxId(Number.parseInt(result.txid))
    },
  })
  logger.debug('Executing createBoardTx transaction', { payload, context })
  return createBoardTx({ payload, context })
}
